/**
 * TableFullscreenModal — renders a chat-generated table in a full-viewport overlay.
 *
 * Props:
 *   open          boolean
 *   tableHtml     string | null   — the raw <table>…</table> HTML
 *   columns       string[]        — column names (parsed from headers)
 *   rows          object[]        — data rows  (parsed from <td>s)
 *   sourceQuery   string          — the chat question that produced this table
 *   onClose       () => void
 *   onSave        ({ name, columns, rows, sourceQuery }) => void
 *
 * Design: editorial overlay matching the platform aesthetic.
 * Background: near-black ink at 0.75 opacity.
 * Content panel: paper white, full viewport, thin top border in accent.
 */
import { useEffect, useRef, useState } from "react";
import { X, Maximize2, Save, Check, ChevronLeft, ChevronRight } from "lucide-react";
import { createPortal } from "react-dom";

const PAGE_SIZE = 200;

export default function TableFullscreenModal({
  open,
  tableHtml,
  columns = [],
  rows = [],
  sourceQuery = "",
  onClose,
  onSave,
}) {
  const [page, setPage]             = useState(0);
  const [saving, setSaving]         = useState(false);
  const [saved, setSaved]           = useState(false);
  const [nameInput, setNameInput]   = useState("");
  const [showNameInput, setShowNameInput] = useState(false);
  const nameRef = useRef(null);

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pageRows   = rows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (open) {
      setPage(0);
      setSaved(false);
      setSaving(false);
      setShowNameInput(false);
      setNameInput(sourceQuery.length > 60 ? sourceQuery.slice(0, 60) + "…" : sourceQuery);
    }
  }, [open, sourceQuery]);

  // Focus name input when shown
  useEffect(() => {
    if (showNameInput) nameRef.current?.focus();
  }, [showNameInput]);

  // Escape key to close
  useEffect(() => {
    if (!open) return;
    function onKey(e) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  function handleSaveClick() {
    if (saved) return;
    if (!showNameInput) {
      setShowNameInput(true);
      return;
    }
    // Commit the save
    const name = nameInput.trim() || sourceQuery || "Saved table";
    setSaving(true);
    onSave({ name, columns, rows, sourceQuery });
    setSaving(false);
    setSaved(true);
    setShowNameInput(false);
  }

  return createPortal(
    <div
      className="fullscreen-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Full-screen table view"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="fullscreen-panel">
        {/* ── Header ────────────────────────────────────────────────────── */}
        <div className="fullscreen-header">
          <div className="flex items-center gap-3 min-w-0">
            <Maximize2 className="w-4 h-4 shrink-0 text-[var(--color-ink-3)]" />
            <div className="min-w-0">
              <div className="eyebrow" style={{ fontSize: "9px" }}>TABLE · FULL VIEW</div>
              {sourceQuery && (
                <p
                  className="truncate text-[var(--color-ink-2)]"
                  style={{ fontSize: "var(--text-xs)", marginTop: "2px" }}
                  title={sourceQuery}
                >
                  {sourceQuery}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {/* Save flow */}
            {showNameInput && (
              <input
                ref={nameRef}
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSaveClick();
                  if (e.key === "Escape") setShowNameInput(false);
                }}
                placeholder="Table name…"
                className="fullscreen-name-input"
                style={{ fontFamily: "var(--font-sans)", fontSize: "var(--text-xs)" }}
              />
            )}

            {onSave && !saved && (
              <button
                onClick={handleSaveClick}
                disabled={saving}
                className="btn btn-ghost"
                style={{ fontSize: "var(--text-xs)", paddingBlock: "5px" }}
                title="Save to My Tables"
              >
                <Save className="w-3.5 h-3.5" />
                {showNameInput ? "Confirm" : "Save table"}
              </button>
            )}

            {saved && (
              <span
                className="flex items-center gap-1.5"
                style={{ fontSize: "var(--text-xs)", color: "var(--color-status-even)" }}
              >
                <Check className="w-3.5 h-3.5" />
                Saved
              </span>
            )}

            <button
              onClick={onClose}
              className="fullscreen-close"
              aria-label="Close full-screen view"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* ── Meta bar ──────────────────────────────────────────────────── */}
        <div className="fullscreen-meta">
          <span className="mono">{rows.length.toLocaleString()} rows · {columns.length} columns</span>
          {totalPages > 1 && (
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setPage((p) => p - 1)}
                disabled={page === 0}
                className="fullscreen-page-btn"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>
              <span className="mono">{page + 1} / {totalPages}</span>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= totalPages - 1}
                className="fullscreen-page-btn"
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>

        {/* ── Table ─────────────────────────────────────────────────────── */}
        <div className="fullscreen-table-wrap">
          <table className="fullscreen-table">
            <thead>
              <tr>
                {columns.map((col) => (
                  <th key={col}>{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pageRows.map((row, i) => (
                <tr key={i} className={i % 2 !== 0 ? "fullscreen-row-alt" : ""}>
                  {columns.map((col) => (
                    <td key={col}>{row[col] ?? "—"}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Parse an HTML <table> string into { columns, rows }.
 * Used by ChatPage to extract data from a rendered markdown table.
 */
export function parseHtmlTable(tableHtml) {
  if (typeof document === "undefined") return { columns: [], rows: [] };

  const wrapper = document.createElement("div");
  wrapper.innerHTML = tableHtml;
  const table = wrapper.querySelector("table");
  if (!table) return { columns: [], rows: [] };

  // Columns from <th>
  const ths = Array.from(table.querySelectorAll("thead th, tr:first-child th"));
  const columns = ths.map((th) => th.textContent.trim());

  // Rows from <tbody tr>
  const trs = Array.from(table.querySelectorAll("tbody tr"));
  const rows = trs.map((tr) => {
    const tds = Array.from(tr.querySelectorAll("td"));
    return Object.fromEntries(columns.map((col, i) => [col, tds[i]?.textContent.trim() ?? ""]));
  });

  return { columns, rows };
}
